import * as React from "react";
import {
  Box,
  Divider,
  Stack,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useContext } from "react";
import Modal from "../Modal/Modal";
import { de } from "date-fns/locale";
import { format } from "date-fns/format";
import PageTitle from "../Displays/PageTitle";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useToggle } from "src/hooks/useToggle";
import { Tariff } from "src/models/clientsModel";
import { ThemeValues } from "../ThemeUsage/theme";
import { dateFormat } from "src/helpers/dateFormat";
import { ThemeContext } from "../ThemeUsage/ThemeUsage";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import ModalContent from "src/pages/Tarif/components/ModalContent";
import { BONUS, SESSION_KEYS, SITE_TECHNICAL_DATA } from "src/consts/session";
import DetailsModalContent from "src/pages/Tarif/components/DetailsModalContent";
import { QuestionMark } from "src/assets";
import CheckWhite from "src/assets/icons/checkWhite.svg";
import Check from "src/assets/icons/check.svg";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface TariffTimelineProps {
  tariffsData?: Tariff[];
  showSavingsResult: boolean;
}

const TariffTimeline = ({
  tariffsData,
  showSavingsResult,
}: TariffTimelineProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const themeName = useContext(ThemeContext);
  const currentMonth = format(new Date(), "LLLL", { locale: de });

  const [getSiteTechnicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const campaignBonusDisplayName = getBonus()?.logicalId ?? "";
  const campaignBonusValidTill = getBonus()?.endDate ?? "";

  const showCampaignText = campaignBonusDisplayName.length > 0;
  const showBonusValidTillText = campaignBonusValidTill.length > 0;
  const bonusValidTillDate =
    showBonusValidTillText && dateFormat(campaignBonusValidTill);
  const isSmartMeterInstalled =
    (getSiteTechnicalData()?.smartMeterInstalled ?? "") === "true";

  const [isOverlayOpenFix, toggleIsOverlayOpenFix] = useToggle(false);
  const [isOverlayOpenFlex, toggleIsOverlayOpenFlex] = useToggle(false);

  const savingsData = tariffsData && tariffsData[0];
  const fixTariffDisplayName =
    (savingsData && savingsData.fixTariff?.tariffDisplayName) ?? "";
  const fixBasePriceMonthly =
    (savingsData && savingsData.fixTariff?.basePriceMonthly) ?? "";
  const fixWorkingPricePerKwh =
    (savingsData && savingsData.fixTariff?.workingPricePerKwh) ?? "";
  const fixMonthlyCost =
    (savingsData && savingsData.fixTariff?.monthlyCosts) ?? "";

  const costSmartMeterMonthly =
    savingsData && savingsData.savings?.costSmartMeterMonthly;
  const flexTariffDisplayName =
    (savingsData && savingsData.flexTariff?.tariffDisplayName) ?? "";

  const savingsYearly = savingsData && savingsData.savings?.savingsYearly;
  const flexBasePriceMonthly =
    (savingsData && savingsData.flexTariff?.basePriceMonthly) ?? "";
  const flexWorkingPricePerKwh =
    (savingsData && savingsData.flexTariff?.workingPricePerKwh) ?? "";
  const flexSupplyPriceFixedElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFixedElementPerKwh) ??
    "";
  const flexSupplyPriceFlexElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFlexElementPerKwh) ?? "";

  const estimatedYearlyCostsRaw = savingsData?.flexTariff?.estimatedYearlyCosts;

  const estimatedYearlyCostsNumber: number | null =
    estimatedYearlyCostsRaw != null
      ? Number(String(estimatedYearlyCostsRaw).replace(",", "."))
      : null;

  const estimatedMonthlyCosts: string =
    estimatedYearlyCostsNumber !== null
      ? (estimatedYearlyCostsNumber / 12).toFixed(2).replace(".", ",")
      : "-- ; --";

  const isLichtblick = themeName === ThemeValues.lichtblick;
  const cardBgColor = isLichtblick ? "#FFFFFF" : "#DAECFA";

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        gap={3}
        sx={{ width: "100%", alignItems: "flex-start" }}
      >
        <Box sx={{ flex: 1, width: "100%" }}>
          <Stack gap={2} sx={{ position: "relative", zIndex: 2 }}>
            <Stack
              sx={{
                bgcolor: cardBgColor,
                borderRadius: "12px",
                padding: { xs: "5px", sm: "15px" },
              }}
            >
              <Stack
                sx={{
                  width: "100%",
                  alignSelf: "center",
                  backgroundColor: isLichtblick ? "#BFCEDE" : "#084465",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    backgroundColor: isLichtblick ? "#30475C" : "#105A83",
                    px: 3,
                    py: 3,
                    width: { xs: "100%", sm: "98%" },
                    margin: "0 auto",
                    marginTop: { xs: "0px", sm: "8px" },
                    borderRadius: "8px",
                  }}
                >
                  <Stack
                    direction="column"
                    alignItems="flex-start"
                    gap={{ xs: 1, sm: 2 }}
                  >
                    <Text
                      sx={{
                        color: isLichtblick ? "#F7BCCE" : "#FFFFFF",
                        fontSize: { xs: "12px", sm: "16px" },
                        fontWeight: 700,
                        lineHeight: "20px",
                      }}
                      text={flexTariffDisplayName}
                    />
                  </Stack>

                  <Stack
                    direction="column"
                    alignItems="flex-end"
                    sx={{ color: "#FFFFFF" }}
                  >
                    <Stack direction="row">
                      <Stack gap={0} direction="row" alignItems="center">
                        <Text
                          sx={{
                            color: isLichtblick ? "#F7BCCE" : "#FFFFFF",
                            fontSize: { xs: "15px", sm: "20px" },
                            fontWeight: 900,
                          }}
                          text={<>{`${estimatedMonthlyCosts} €/mtl.`}</>}
                        />
                        <Text
                          sx={{
                            color: isLichtblick ? "#F7BCCE" : "#FFFFFF",
                            marginTop: -1,
                            fontSize: { xs: "8px", sm: "10px" },
                            fontFamily: "isonType",
                            fontWeight: 700,
                          }}
                          text={`1`}
                        />
                      </Stack>
                    </Stack>

                    <Text
                      sx={{
                        color: "#FFFFFF",
                        fontSize: { xs: "8px", sm: "10px" },
                        mt: -1,
                      }}
                      text={t(
                        "page.tariff.tariffSelectionContainer.calculatedCosts",
                      )}
                    />
                  </Stack>
                </Stack>

                <Stack gap={3} sx={{ px: 3, pt: 3 }}>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Text
                      sx={{
                        color: isLichtblick ? "#000000" : "#FFFFFF",
                        fontSize: { xs: "11px", sm: "13px" },
                        fontWeight: "600",
                        lineHeight: "15px",
                      }}
                      text={`${t("page.tariff.timeline.timelineItem3.list.listItem2")}`}
                    />
                    <Text
                      sx={{
                        color: isLichtblick ? "#000000" : "#FFFFFF",
                        fontSize: { xs: "11px", sm: "13px" },
                        fontWeight: "600",
                        lineHeight: "15px",
                      }}
                      text={`${flexWorkingPricePerKwh} ct/kWh`}
                    />
                  </Stack>
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Text
                      sx={{
                        color: isLichtblick ? "#000000" : "#FFFFFF",
                        fontSize: { xs: "9px", sm: "11px" },
                        fontWeight: "400",
                        lineHeight: "15px",
                      }}
                      text={`${t("page.tariff.timeline.timelineItem3.list.listItem3")}`}
                    />
                    <Text
                      sx={{
                        color: isLichtblick ? "#000000" : "#FFFFFF",
                        fontSize: { xs: "9px", sm: "11px" },
                        fontWeight: "400",
                        lineHeight: "15px",
                      }}
                      text={`${flexSupplyPriceFixedElementPerKwh} ct/kWh`}
                    />
                  </Stack>
                  <Stack>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Text
                        sx={{
                          color: isLichtblick ? "#000000" : "#FFFFFF",
                          fontSize: { xs: "9px", sm: "11px" },
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem4")}`}
                      />{" "}
                      <Text
                        sx={{
                          color: isLichtblick ? "#000000" : "#FFFFFF",
                          fontSize: { xs: "9px", sm: "11px" },
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${flexSupplyPriceFlexElementPerKwh} ct/kWh`}
                      />
                    </Stack>
                    <Stack direction={"row"} gap={1}>
                      <Text
                        sx={{
                          color: isLichtblick ? "#000000" : "#FFFFFF",
                          fontSize: { xs: "9px", sm: "11px" },
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem12")}`}
                      />
                    </Stack>
                  </Stack>
                  <Divider
                    sx={{ borderColor: isLichtblick ? "#000000" : "#C6C6C633" }}
                  />
                  <Stack direction={"row"} justifyContent={"space-between"}>
                    <Text
                      sx={{
                        color: isLichtblick ? "#000000" : "#FFFFFF",
                        fontSize: { xs: "11px", sm: "13px" },
                        fontWeight: "600",
                        lineHeight: "15px",
                      }}
                      text={`${t("page.tariff.timeline.timelineItem3.list.listItem1")}`}
                    />
                    <Text
                      sx={{
                        color: isLichtblick ? "#000000" : "#FFFFFF",
                        fontSize: { xs: "11px", sm: "13px" },
                        fontWeight: "600",
                        lineHeight: "15px",
                      }}
                      text={`${flexBasePriceMonthly} ${t("page.tariff.timeline.timelineItem3.list.listItem1Value")}`}
                    />
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  sx={{
                    py: 2,
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    backgroundColor: isLichtblick ? "#BFCEDE" : "#084465",
                  }}
                >
                  <Stack
                    direction="row"
                    mt={2}
                    sx={{ padding: 0, px: { xs: 2, sm: 4 } }}
                    width="100%"
                    justifyContent="center"
                    gap={{ xs: 3, sm: 6 }}
                  >
                    {[
                      t("page.calculatedSavingsSummary.checkbox.point1"),
                      t("page.calculatedSavingsSummary.checkbox.point3"),
                    ].map((feature, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 1, sm: 2 }}
                      >
                        <Box
                          component="img"
                          src={(isLichtblick ? Check : CheckWhite) as string}
                          sx={{
                            width: 10,
                            height: 10,
                          }}
                        />
                        <Text
                          text={feature}
                          sx={{
                            fontSize: { xs: "9px", sm: "10px" },
                            color: isLichtblick ? "#000000" : "#FFFFFF",
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Stack>

              <Stack
                sx={(theme) => ({
                  py: 3,
                  gap: 4,
                  bgcolor: isLichtblick
                    ? theme.palette.app.secondary
                    : "#DAECFA",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  paddingX: { xs: "2px", sm: "0px" },
                })}
              >
                <Stack gap={2}>
                  <Stack gap={1}>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Text
                        sx={{
                          color: "#3B3B3B",
                          fontSize: "11px",
                          fontWeight: "500",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem6")}`}
                      />
                      <Text
                        sx={{
                          color: "#3B3B3B",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem6Value")}`}
                      />
                    </Stack>
                  </Stack>

                  <Divider />

                  <Stack gap={1}>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Text
                        sx={{
                          color: "#222",
                          fontSize: "11px",
                          fontWeight: "600",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem7")}`}
                      />
                    </Stack>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Text
                        sx={{
                          color: "#3B3B3B",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem8")}`}
                      />
                      <Text
                        sx={{
                          color: "#3B3B3B",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem7Value")}`}
                      />
                    </Stack>
                    <Stack direction={"row"} justifyContent={"space-between"}>
                      <Text
                        sx={{
                          color: "#3B3B3B",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem9")}`}
                      />
                      <Text
                        sx={{
                          color: "#3B3B3B",
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                        }}
                        text={`${t("page.tariff.timeline.timelineItem3.list.listItem9Value")}`}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      sx={{ mt: 6 }}
                    >
                      <Text
                        onClick={toggleIsOverlayOpenFlex}
                        sx={{
                          color: "#222",
                          fontSize: "12px",
                          fontWeight: 700,
                          lineHeight: "15px",
                          cursor: "pointer",
                        }}
                        text={t("page.tariff.timeline.timelineItem3.modalText")}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>

              {showSavingsResult && (
                <Stack
                  direction={"column"}
                  sx={{
                    mt: 3,
                    borderRadius: 3,
                    bgcolor: isLichtblick ? "#BEEDD5" : "#C6FFB2",
                    border: isLichtblick ? undefined : "1px solid #039C25",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      borderRadius: "12px",
                      px: 3,
                      py: 2,
                    }}
                  >
                    <Text
                      sx={{
                        color: "#222222",
                        fontSize: { xs: "11px", sm: "14px" },
                        fontWeight: 400,
                      }}
                      text={t("page.calculatedSavingsSummary.approxSavings")}
                    />

                    <Stack direction="row" gap={0.5} alignItems="center">
                      <Text
                        sx={{
                          color: "#31825E",
                          fontSize: { xs: "11px", sm: "14px" },
                          fontWeight: 900,
                        }}
                        text={`${savingsYearly} €`}
                      />
                      <Text
                        sx={{
                          color: "#009A22",
                          marginTop: -3,
                          marginLeft: -0.5,
                          fontSize: { xs: "8px", sm: "10px" },
                          fontFamily: "isonType",
                          fontWeight: 700,
                        }}
                        text={`1`}
                      />
                    </Stack>
                  </Stack>

                  {showCampaignText && (
                    <Stack sx={{ width: 1 }}>
                      <Stack
                        alignItems={"center"}
                        sx={{
                          mt: themeName === ThemeValues.SMA ? 10 : 8,
                          borderRadius: 3,
                          bgcolor: "#48BD57",
                          border: "1px solid #48BD57",
                          margin: 1,
                          padding: 1,
                        }}
                      >
                        {showBonusValidTillText && (
                          <Text
                            text={`${t(`page.tariff.timeline.onlyTill`)} ${bonusValidTillDate}`}
                            sx={{
                              mb: -2,
                              color: "#E9FFE1",
                              fontSize: "15px",
                              fontWeight: 400,
                              lineHeight: "25px",
                            }}
                          />
                        )}

                        <Text
                          text={
                            <>
                              {t(
                                `page.tariff.timeline.bonusSavings.${campaignBonusDisplayName}` as any,
                              )}
                              <sup>4</sup>
                            </>
                          }
                          sx={{
                            color: "#E9FFE1",
                            fontSize: "20px",
                            fontWeight: 700,
                            lineHeight: "25px",
                          }}
                        />
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, width: "100%" }}>
          <Stack gap={0} sx={{ width: "100%" }}>
            <Stack
              sx={{
                bgcolor: cardBgColor,
                padding: { xs: "10px", sm: "16px" },
                width: "100%",
                boxSizing: "border-box",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                borderBottomLeftRadius: isSmartMeterInstalled ? "12px" : "0px",
                borderBottomRightRadius: isSmartMeterInstalled ? "12px" : "0px",
              }}
            >
              <Accordion
                disableGutters
                elevation={0}
                square
                sx={{
                  "&:before": { display: "none" },
                  bgcolor: "transparent",
                  borderRadius: "0px",
                  "&.Mui-expanded": {
                    borderBottom: "1px solid #222222",
                    pb: 1,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  sx={{
                    px: 0,
                    "& .MuiAccordionSummary-content": { margin: 0 },
                  }}
                >
                  <Stack gap={0.5}>
                    <Text
                      sx={{
                        fontSize: "12px",
                        fontWeight: "700",
                        lineHeight: "16px",
                        color: "#222222",
                      }}
                      text={`${t(
                        "page.tariff.timeline.timelineItem1.seperatorText",
                      )} ${fixTariffDisplayName}`}
                    />
                    <Stack gap={0.5} direction="row" alignItems="center">
                      <Text
                        sx={{
                          fontSize: "11px",
                          fontWeight: "400",
                          lineHeight: "15px",
                          color: "#666666",
                        }}
                        text={`${t(
                          "page.tariff.timeline.timelineItem1.subtitle",
                        )}: ${fixMonthlyCost}€/mtl.`}
                      />
                      <Text
                        sx={{
                          fontSize: "8px",
                          marginTop: -1,
                          marginLeft: -0.5,
                          fontFamily: "isonType",
                          color: "#666666",
                        }}
                        text="2"
                      />
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 0, pt: 0.5 }}>
                  <Stack gap={3}>
                    <Text
                      sx={{
                        fontSize: "11px",
                        fontWeight: "400",
                        lineHeight: "16px",
                        color: "#3B3B3B",
                      }}
                      text={
                        <>
                          {t("page.tariff.timeline.timelineItem1.description")}
                          <QuestionMark
                            onClick={toggleIsOverlayOpenFix}
                            style={{
                              verticalAlign: "middle",
                              marginLeft: "8px",
                              cursor: "pointer",
                            }}
                          />
                        </>
                      }
                    />

                    <Stack gap={1}>
                      <Text
                        sx={{
                          fontSize: "11px",
                          fontWeight: "700",
                          lineHeight: "15px",
                          color: "#222222",
                        }}
                        text={t(
                          "page.tariff.timeline.timelineItem1.list.title",
                        )}
                      />

                      <Stack direction="row" justifyContent="space-between">
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                            color: "#3B3B3B",
                          }}
                          text={`${t(
                            "page.tariff.timeline.timelineItem1.list.listItem1",
                          )}:`}
                        />
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                            color: "#3B3B3B",
                          }}
                          text={t(
                            "page.tariff.timeline.timelineItem1.list.listItem1Value",
                            { fixBasePriceMonthly },
                          )}
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "500",
                            lineHeight: "15px",
                            color: "#3B3B3B",
                          }}
                          text={`${t(
                            "page.tariff.timeline.timelineItem1.list.listItem2",
                          )} (${currentMonth}):`}
                        />
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "400",
                            lineHeight: "15px",
                            color: "#3B3B3B",
                          }}
                          text={`${fixWorkingPricePerKwh} ct/kWh`}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>

            {!isSmartMeterInstalled && (
              <Stack
                sx={{
                  bgcolor: cardBgColor,
                  borderBottomLeftRadius: "12px",
                  borderBottomRightRadius: "12px",
                  padding: { xs: "10px", sm: "16px" },
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Accordion
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    "&:before": { display: "none" },
                    bgcolor: "transparent",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                    sx={{
                      px: 0,
                      "& .MuiAccordionSummary-content": { margin: 0 },
                    }}
                  >
                    <Text
                      sx={{
                        fontSize: "11px",
                        fontWeight: "700",
                        lineHeight: "16px",
                        color: "#222222",
                      }}
                      text={t(
                        "page.tariff.timeline.timelineItem2.seperatorText",
                      )}
                    />
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 0, pt: 0.5 }}>
                    <Stack gap={3}>
                      <Stack gap={3}>
                        <Stack
                          direction="row"
                          gap={1.5}
                          alignItems="flex-start"
                        >
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={t(
                              "page.tariff.timeline.timelineItem2.description1",
                            )}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          gap={1.5}
                          alignItems="flex-start"
                        >
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={t(
                              "page.tariff.timeline.timelineItem2.description2",
                            )}
                          />
                        </Stack>

                        <Stack
                          direction="row"
                          gap={1.5}
                          alignItems="flex-start"
                        >
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={t(
                              "page.tariff.timeline.timelineItem2.description3",
                            )}
                          />
                        </Stack>
                      </Stack>

                      <Stack gap={1}>
                        <Text
                          sx={{
                            fontSize: "11px",
                            fontWeight: "700",
                            lineHeight: "15px",
                            color: "#222222",
                          }}
                          text={t(
                            "page.tariff.timeline.timelineItem2.list.title",
                          )}
                        />

                        <Stack direction="row" justifyContent="space-between">
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={`${t(
                              "page.tariff.timeline.timelineItem2.list.listItem1",
                            )}:`}
                          />

                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text="0,00 €"
                          />
                        </Stack>

                        <Stack direction="row" justifyContent="space-between">
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={`${t(
                              "page.tariff.timeline.timelineItem2.list.listItem2",
                            )}:`}
                          />

                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={`${costSmartMeterMonthly} ${t(
                              "page.tariff.timeline.timelineItem2.list.listItem2Value",
                            )}`}
                          />
                        </Stack>

                        <Stack direction="row" justifyContent="space-between">
                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "500",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={`${t(
                              "page.tariff.timeline.timelineItem2.list.listItem3",
                            )}:`}
                          />

                          <Text
                            sx={{
                              fontSize: "11px",
                              fontWeight: "400",
                              lineHeight: "15px",
                              color: "#3B3B3B",
                            }}
                            text={t(
                              "page.tariff.timeline.timelineItem2.list.listItem3Value",
                            )}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.tariff.electricity.modalTitle1", {
              fixTariffDisplayName,
            })}
          />
        }
        isOpen={isOverlayOpenFix}
        onClose={toggleIsOverlayOpenFix}
        onSubmit={toggleIsOverlayOpenFix}
      >
        <DetailsModalContent />
      </Modal>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.tariff.electricity.modalTitle2", {
              flexTariffDisplayName,
            })}
          />
        }
        isOpen={isOverlayOpenFlex}
        onClose={toggleIsOverlayOpenFlex}
        onSubmit={toggleIsOverlayOpenFlex}
      >
        <ModalContent />
      </Modal>
    </>
  );
};

export default TariffTimeline;
